import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Score } from '@/components/Score';
import { Alert } from '@/components/ui/Alert';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { ProgressBar, ProgressRow } from '@/components/ui/Navigation';
import { SkeletonCard } from '@/components/ui/States';
import { BandTrend } from '@/features/progress/BandTrend';
import { WeakAreaList } from '@/features/progress/WeakAreaList';
import { isPracticeSection, type PracticeSection } from '@/features/practice/practice-sections';

export const Route = createFileRoute('/progress/$skill')({
  validateSearch: (search: Record<string, unknown>) => ({
    variant: typeof search['variant'] === 'string' ? search['variant'] : undefined,
  }),
  component: ProgressSkillPage,
});

const CORE_SKILLS = ['listening', 'reading', 'writing', 'speaking'] as const;

function practiceSectionForSkill(skill: string): PracticeSection | undefined {
  return isPracticeSection(skill) ? skill : undefined;
}

function ProgressSkillPage() {
  const { skill } = Route.useParams();
  const { variant: variantSearch } = Route.useSearch();

  const detailQuery = useQuery({
    queryKey: ['progress-skill', skill, variantSearch],
    queryFn: () =>
      api.get('/api/v1/progress/skills/{skill}', {
        params: { skill },
        ...(variantSearch ? { query: { variant: variantSearch } } : {}),
      }),
  });

  if (detailQuery.isPending) {
    return (
      <div className="grid gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <Alert tone="danger" title="We could not load this skill.">
        Reload the page to try again.
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const variantSlug = detail.variant.slug;
  const practiceSection = practiceSectionForSkill(detail.skill);
  const trend = detail.detail.history.map((point) => ({
    submittedAt: point.submittedAt,
    value: point.score.value,
  }));

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow={detail.variant.name}
        title={detail.skill.charAt(0).toUpperCase() + detail.skill.slice(1)}
        description="Where your marks come from, and what is holding the band down."
        actions={
          practiceSection ? (
            <Link
              to="/practice"
              search={{ variant: variantSlug, section: practiceSection }}
              className={buttonClassName()}
            >
              Practise {detail.skill}
            </Link>
          ) : null
        }
      />

      <ExamSurfaceDisclaimer text={detail.variant.trademarkDisclaimer} />

      <nav aria-label="Skills" className="flex gap-1 overflow-x-auto border-b border-border">
        <Link
          to="/progress"
          search={{ variant: variantSlug }}
          className="-mb-px h-11 shrink-0 border-b-2 border-transparent px-3.5 text-[15px] font-bold leading-[2.75rem] text-muted hover:text-ink"
        >
          Overview
        </Link>
        {CORE_SKILLS.map((row) => (
          <Link
            key={row}
            to="/progress/$skill"
            params={{ skill: row }}
            search={{ variant: variantSlug }}
            aria-current={row === detail.skill ? 'page' : undefined}
            className={
              row === detail.skill
                ? '-mb-px h-11 shrink-0 border-b-2 border-primary px-3.5 text-[15px] font-bold leading-[2.75rem] text-primary'
                : '-mb-px h-11 shrink-0 border-b-2 border-transparent px-3.5 text-[15px] font-bold leading-[2.75rem] text-muted hover:text-ink'
            }
          >
            {capitalise(row)}
          </Link>
        ))}
      </nav>

      {detail.detail.latest ? (
        <Card padding="lg" className="grid gap-3">
          <h2 className="font-display text-h3">Latest score</h2>
          <Score score={detail.detail.latest} size="lg" />
        </Card>
      ) : null}

      {trend.length >= 2 ? (
        <Card padding="lg" className="grid gap-4">
          <h2 className="font-display text-h3">Band over time</h2>
          <BandTrend points={trend} label={`${capitalise(detail.skill)} band over time`} />
        </Card>
      ) : null}

      <section aria-labelledby="skill-task-types" className="grid gap-4">
        <h2 id="skill-task-types" className="font-display text-h3">
          Question types
        </h2>
        {detail.detail.taskTypes.length === 0 ? (
          <p className="text-body text-muted">
            We need more answers before we can break this down by question type.
          </p>
        ) : (
          <Card padding="lg" className="grid gap-4">
            {detail.detail.taskTypes.map((row) => (
              <ProgressRow
                key={row.taskTypeId}
                label={row.taskTypeId}
                hint={`${Math.round(row.accuracy * 100)}% of ${row.n} questions`}
              >
                <ProgressBar
                  value={row.accuracy}
                  label={row.taskTypeId}
                  tone={row.accuracy < 0.6 ? 'warning' : 'primary'}
                />
              </ProgressRow>
            ))}
          </Card>
        )}
      </section>

      {detail.detail.criteria.status === 'skipped' ? null : (
        <section aria-labelledby="skill-criteria" className="grid gap-4">
          <h2 id="skill-criteria" className="font-display text-h3">
            Marking criteria
          </h2>
          {detail.detail.criteria.rows.length === 0 ? (
            <p className="text-body text-muted">No criterion marks yet.</p>
          ) : (
            <Card padding="lg" className="grid gap-4">
              {detail.detail.criteria.rows.map((row) => (
                <ProgressRow
                  key={row.criterionId}
                  label={row.label}
                  hint={`average ${row.mean.toFixed(1)} across ${row.responses} answers`}
                >
                  <ProgressBar value={row.mean} max={9} label={row.label} />
                </ProgressRow>
              ))}
            </Card>
          )}
        </section>
      )}

      <section aria-labelledby="skill-weak" className="grid gap-4">
        <h2 id="skill-weak" className="font-display text-h3">
          Weak areas
        </h2>
        <WeakAreaList weakAreas={detail.weakAreas} />
      </section>

      {detail.insufficient.length > 0 ? (
        <Alert tone="info" title="We need a few more answers before some breakdowns appear.">
          {detail.insufficient
            .map((row) => `${row.taskTypeId}: ${row.n} of ${row.n + row.needed}`)
            .join(' · ')}
        </Alert>
      ) : null}
    </div>
  );
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
